var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { DateTime } from 'components/DateTime';
import { Icon } from 'components/Icon';
import { Image } from 'components/Image';
import { Money } from 'components/Money';
import { Text } from 'components/Text';
import i18n from 'components/translation/i18n';
import { formatFieldValue, hasDisplayableCustomFieldValue } from 'helpers/Field';
import { classNames } from 'libs/react';
import { React } from 'libs/react';
import { QuoteMedia } from 'modules/public/quote/components/QuoteMedia';
import { DealItemDiscountedPrice } from './DealItemDiscountedPrice';
import './DealItemMobileCard.scss';
/**
 * The rows of the custom fields holding a value. An empty field keeps its
 * column in the desktop table, but is only noise in a card.
 * @function getDealItemMobileFieldRows
 * @since 1.0.0
 */
export function getDealItemMobileFieldRows(fields, fieldValues) {
    const rows = [];
    fields.forEach((field) => {
        var _a, _b, _c;
        const value = (_a = fieldValues === null || fieldValues === void 0 ? void 0 : fieldValues.find((fieldValue) => { var _a; return ((_a = fieldValue.field) === null || _a === void 0 ? void 0 : _a.id) === field.id; })) === null || _a === void 0 ? void 0 : _a.value;
        if (!hasDisplayableCustomFieldValue(value, field, i18n.language)) {
            return;
        }
        if (field.type === 'FILE') {
            rows.push({
                key: field.id,
                label: (_b = field.title) !== null && _b !== void 0 ? _b : '',
                interactive: true,
                value: (React.createElement(QuoteMedia, { style: "normal", value: formatFieldValue(value, field.type, field), noLabel: true }))
            });
            return;
        }
        rows.push({
            key: field.id,
            label: (_c = field.title) !== null && _c !== void 0 ? _c : '',
            value: (React.createElement(Text, { value: formatFieldValue(value, field.type, field, i18n.language) }))
        });
    });
    return rows;
}
/**
 * A line of a read-only quote below the md breakpoint, where the table of the
 * desktop layout does not fit.
 * @component DealItemMobileCard
 * @since 1.0.0
 */
export function DealItemMobileCard(props) {
    const { actions, image, meta, onPress, picto, price, rows, title } = props;
    const metaText = meta === null || meta === void 0 ? void 0 : meta.filter(Boolean).join(' · ');
    const className = classNames('deal-item-panel-group__content--mobile', 'deal-item-mobile-card', { 'deal-item-mobile-card--pressable': !!onPress }, props.className);
    return (React.createElement("div", { className: className, onClick: onPress },
        React.createElement("div", { className: "deal-item-mobile-card-head" },
            props.unavailable ? (React.createElement("div", { className: "deal-item-mobile-card-media" },
                React.createElement(Icon, { value: "exclamation-triangle", style: "danger", tooltip: t('This item is no longer available.') }))) : image ? (React.createElement("div", { className: "deal-item-mobile-card-media" },
                React.createElement(Image, { source: image, cover: true }))) : picto ? (React.createElement("div", { className: "deal-item-mobile-card-media" },
                React.createElement(Icon, { value: picto }))) : null,
            React.createElement("div", { className: "deal-item-mobile-card-main" },
                React.createElement(Text, { className: "deal-item-mobile-card-title", value: title }),
                metaText && (React.createElement(Text, { className: "deal-item-mobile-card-meta", value: metaText }))),
            price && React.createElement("div", { className: "deal-item-mobile-card-price" }, price)),
        rows && rows.length > 0 && (React.createElement("div", { className: "deal-item-mobile-card-rows" }, rows.map((row) => (React.createElement("div", { className: "deal-item-mobile-card-row", key: row.key, onClick: row.interactive ? (e) => e.stopPropagation() : undefined },
            React.createElement("div", { className: "deal-item-mobile-card-row-label" }, row.label),
            React.createElement("div", { className: "deal-item-mobile-card-row-value" }, row.value)))))),
        actions && (React.createElement("div", { className: "deal-item-mobile-card-actions", onClick: (e) => e.stopPropagation() }, actions))));
}
/**
 * An option or a package of a read-only quote below the md breakpoint.
 * @component DealConfigurationMobileCard
 * @since 1.0.0
 */
export function DealConfigurationMobileCard(props) {
    const { effectiveQuantity, endTime, fieldValues, fields, files, listPrice, onOpenFile, priceDisplay, pricePerUnit, pricingDisplay, quantity, quantityEditor, quantitySuffix, showDiscount, showQuantity, startTime } = props, cardProps = __rest(props, ["effectiveQuantity", "endTime", "fieldValues", "fields", "files", "listPrice", "onOpenFile", "priceDisplay", "pricePerUnit", "pricingDisplay", "quantity", "quantityEditor", "quantitySuffix", "showDiscount", "showQuantity", "startTime"]);
    const showAmount = priceDisplay === 'AMOUNT' &&
        (pricingDisplay === 'ALL' ||
            pricingDisplay === 'BUNDLED' ||
            pricingDisplay === 'LINE');
    const rows = [];
    if (startTime || endTime) {
        rows.push({
            key: 'time',
            label: t('Time'),
            value: (React.createElement(React.Fragment, null,
                startTime && (React.createElement(DateTime, { date: false, time: true, value: startTime, hh: "2-digit", mm: "2-digit" })),
                startTime && endTime && ' — ',
                endTime && (React.createElement(DateTime, { date: false, time: true, value: endTime, hh: "2-digit", mm: "2-digit" }))))
        });
    }
    rows.push(...getDealItemMobileFieldRows(fields, fieldValues));
    if (quantityEditor) {
        rows.push({
            key: 'quantity',
            label: t('Quantity'),
            value: quantityEditor,
            interactive: true
        });
    }
    else if (showQuantity && (effectiveQuantity !== 1 || quantitySuffix)) {
        rows.push({
            key: 'quantity',
            label: t('Quantity'),
            value: (React.createElement(React.Fragment, null,
                quantity,
                quantitySuffix && ` ${quantitySuffix}`,
                showAmount && (React.createElement(React.Fragment, null,
                    ' × ',
                    React.createElement(Money, { value: pricePerUnit })))))
        });
    }
    if (files && files.length > 0) {
        rows.push({
            key: 'files',
            label: t('Files'),
            interactive: true,
            value: (React.createElement("div", { className: "deal-item-panel-group-content-cell--files" }, files.map((file) => (React.createElement(Icon, { value: "paperclip", onClick: () => onOpenFile === null || onOpenFile === void 0 ? void 0 : onOpenFile(file), key: file.id })))))
        });
    }
    return (React.createElement(DealItemMobileCard, Object.assign({}, cardProps, { rows: rows, price: priceDisplay === 'INCLUDED' ? (React.createElement("span", { className: "deal-item-mobile-card-included" },
            React.createElement(Icon, { value: "hexagon-check", className: "deal-item-panel-group-content-lock-icon" }),
            t('Included'))) : showAmount ? (React.createElement(DealItemDiscountedPrice, { value: pricePerUnit * effectiveQuantity, listPrice: listPrice * effectiveQuantity, showDiscount: showDiscount })) : null })));
}
